<script>
  import { applicationLimitationPresetsStore } from "@stores/applicationLimitationPresetsStore.js";
  import {
    Modal as DangerModal,
    Select,
    SelectItem,
  } from "carbon-components-svelte";
  import DataTableBlack from "@components/DataTableBlack.svelte";
  import Modal from "@components/Modal.svelte";
  import { TextInput } from "paxray-components-svelte";
  import ContentSwitcher from "lib/components/ContentSwitcher.svelte";
  import EditIcon from "../../settings/svg/EditIcon.svelte";
  import RemoveIcon from "../../settings/svg/RemoveIcon.svelte";

  export let idLimitation;
  export let editable = false;
  export let addedApplications = [];

  let page = 1;
  let pageSize = 10;
  let searchValue = "";
  let deleteConfirmationOpen = false;
  let itemToDelete = null;

  const {
    getApplicationLimitationPresets,
    saveApplicationLimitationPresetItem,
    updateDAMLimitation,
    deleteApplicationLimitationPreset,
    closeModal,
    editApplicationLimitationPresetItem,
  } = applicationLimitationPresetsStore;

  $: ({
    applicationLimitationPresets,
    applicationLimitationPresetsType,
    applicationLimitationModalOpen,
    currentApplicationLimitationPresets,
  } = $applicationLimitationPresetsStore);

  $: if (idLimitation) getApplicationLimitationPresets(idLimitation);

  $: {
    if (addedApplications.length > 0) {
      addedApplications.forEach((application) => {
        saveApplicationLimitationPresetItem(application, idLimitation);
      });
      addedApplications = [];
    }
  }

  // Die schwarze Tabelle filtert nicht selbst: Suche ueber Name,
  // Programm/URL und Fenstertitel.
  $: filteredPresets = applicationLimitationPresets.filter((row) => {
    const query = searchValue.trim().toLowerCase();
    if (!query) return true;
    const program = row?.isbrowser ? row?.url : row?.appname;
    return [row?.name, program, row?.windowtitle].some((value) =>
      String(value || "").toLowerCase().includes(query),
    );
  });

  $: tableHeaders = [
    { key: "name", value: "Name" },
    { key: "appname", value: "Program/URL" },
    { key: "windowtitle", value: "Window Title" },
    ...(editable ? [{ key: "menu", value: "Actions" }] : []),
  ];

  const columnActions = {
    menu: [
      {
        icon: EditIcon,
        label: "Edit",
        onClick: (row) => editApplicationLimitationPresetItem(row.id),
      },
      {
        icon: RemoveIcon,
        label: "Delete",
        danger: true,
        onClick: (row) => {
          itemToDelete = row;
          deleteConfirmationOpen = true;
        },
      },
    ],
  };

  async function confirmDelete() {
    if (!itemToDelete?.id) return;
    await deleteApplicationLimitationPreset(itemToDelete.id);
    deleteConfirmationOpen = false;
    itemToDelete = null;
  }
</script>

<!-- Im eigenen Block, wie in LimitationList: Carbon gibt dem Select-Wrapper
     flex: 1 1 auto - als direktes Kind der Flex-Spalte frass er die freie
     Hoehe und schob die Tabelle ans untere Bildschirmende. -->
<div>
  {#if editable}
    <Select
      labelText="Limit Application Recording"
      selected={applicationLimitationPresetsType}
      on:change={(event) => {
        updateDAMLimitation(idLimitation, event.target.value);
      }}
    >
      <SelectItem value="Whitelist" text="Whitelist" />
      <SelectItem value="Blacklist" text="Blacklist" />
    </Select>
  {:else}
    <span class="limitation-type-title">{applicationLimitationPresetsType}</span>
  {/if}
</div>

<DataTableBlack
  headers={tableHeaders}
  rows={filteredPresets}
  totalItems={filteredPresets.length}
  bind:page
  bind:pageSize
  pageSizes={[10, 25, 50]}
  {searchValue}
  slicePage
  sortable
  showNewButton={editable}
  newButtonText="Add Rule"
  newButtonIconLeft={false}
  columnActions={editable ? columnActions : {}}
  on:search={(e) => {
    searchValue = e.detail.value;
    page = 1;
  }}
  on:clearSearch={() => {
    searchValue = "";
    page = 1;
  }}
  on:addNew={() => {
    editApplicationLimitationPresetItem(0);
  }}
>
  <svelte:fragment slot="cell" let:row let:cell>
    {#if cell.key === "appname"}
      {row?.isbrowser ? row.url : row.appname}
    {:else}
      {cell.value || ""}
    {/if}
  </svelte:fragment>
</DataTableBlack>

<Modal
  open={applicationLimitationModalOpen}
  preventCloseOnClickOutside
  modalHeading="{applicationLimitationPresetsType} Application"
  primaryButtonText="Save"
  secondaryButtonText="Cancel"
  autowidth
  actions
  primaryButtonAction={() => {
    saveApplicationLimitationPresetItem(
      currentApplicationLimitationPresets,
      idLimitation,
    );
  }}
  secondaryButtonAction={() => {
    closeModal();
  }}
  on:close={() => {
    closeModal();
  }}
>
  <ContentSwitcher
    bind:selected={currentApplicationLimitationPresets.isbrowser}
    options={["Desktop Application", "Web Application"]}
  ></ContentSwitcher>
  <TextInput
    labelText="Name"
    placeholder="Enter name for rule"
    bind:value={currentApplicationLimitationPresets.name}
  />
  {#if currentApplicationLimitationPresets?.isbrowser}
    <TextInput
      labelText="URL"
      placeholder="Regex to Match the Webapp URL (before replacement rules are applied)"
      bind:value={currentApplicationLimitationPresets.url}
    />
  {:else}
    <TextInput
      labelText="Application Name"
      placeholder="Enter application name (without .exe)"
      bind:value={currentApplicationLimitationPresets.appname}
    />
  {/if}
  <TextInput
    labelText="Window Title"
    placeholder="Regex to Match the Window title (before replacement rules are applied)"
    bind:value={currentApplicationLimitationPresets.windowtitle}
  />
</Modal>

<DangerModal
  danger
  bind:open={deleteConfirmationOpen}
  preventCloseOnClickOutside
  modalHeading="Delete rule?"
  primaryButtonText="Delete"
  secondaryButtonText="Cancel"
  on:click:button--primary={confirmDelete}
  on:click:button--secondary={() => {
    deleteConfirmationOpen = false;
    itemToDelete = null;
  }}
  on:close={() => {
    deleteConfirmationOpen = false;
    itemToDelete = null;
  }}
>
  <p>Do you really want to delete the following rule?</p>
  <p><strong>{itemToDelete?.name || "-"}</strong></p>
</DangerModal>

<style>
  .limitation-type-title {
    font-size: large;
    font-weight: bold;
    color: #333;
  }
</style>
